import React, { useState } from 'react';
import {
  BooleanField,
  BooleanInput,
  Create,
  Edit,
  EditButton,
  Filter,
  FormDataConsumer,
  FunctionField,
  maxLength,
  minLength,
  minValue,
  NullableBooleanInput,
  number,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  required,
  SaveButton,
  SearchInput,
  TextField,
  TextInput,
  Toolbar,
} from 'react-admin';
import withStyles from '@material-ui/core/styles/withStyles';
import { Link } from '@material-ui/core';
import { ZippityDatagrid, ZippitySimpleForm } from '../../components';
import CustomTitle from '../../components/CustomTitle';
import RegionField from './RegionField';
import { RegionInput } from './RegionInput';
import { TimeZoneInput } from './TimeZoneInput';
// import PriceSheetInput from './PriceSheetInput';
import { printFullAddress } from '../customer-address';
import BulkDeleteButton from '../../components/BulkDeleteButton';
import ReferenceChild from '../../components/ReferenceChild';
import { getServiceEntity, getZ3PConfigValue, perPage } from '../../helper';
import SearchLocationInput from '../customer/SearchLocationInput';
import AddressFields from './AddressFields';
import ListWithCSVImport from '../../components/ListWithCSVImport';

const serviceLocations = getZ3PConfigValue('service_locations');
const hasWorkplaceServices = serviceLocations?.includes('workplace');

const ClientLocationFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
    {/* <PriceSheetInput isRequired={false} {...props} /> */}
    <NullableBooleanInput source="is_active" />
    {hasWorkplaceServices && (
      <>
        {RegionInput(false)}
        {TimeZoneInput(false)}
        <ReferenceInput
          source="default_mechanical_service_vehicle_id"
          reference="service-vehicle"
          sort={{ field: 'name', order: 'ASC' }}
          filter={{ is_active: true }}
          perPage={perPage}
          label={`Default ${getServiceEntity()}`}
        >
          <ReferenceChild
            {...props}
            optionText="name"
            source="default_mechanical_service_vehicle_id"
          />
        </ReferenceInput>
      </>
    )}
  </Filter>
);

const listStyles = {};

const isUsingHelpText = getZ3PConfigValue('is_using_help_text');

export const ClientLocationList = withStyles(listStyles)(
  ({ classes, ...props }) => (
    <div>
      <ListWithCSVImport
        {...props}
        filters={<ClientLocationFilter />}
        bulkActionButtons={<BulkDeleteButton />}
        sort={{ field: 'client_location_id', order: 'ASC' }}
        perPage={50}
      >
        <ZippityDatagrid>
          <TextField source="created_at" />
          <TextField source="modified_at" />
          <TextField source="client_location_id" />
          <BooleanField source="is_active" />
          <TextField source="long_name" />
          <TextField source="default_mechanical_service_vehicle_name" />
          {/* <UrlField source="logo_image_url" /> */}
          {/* <ReferenceField source="price_sheet_id" reference="price-sheet">
            <TextField source="price_sheet_name" />
          </ReferenceField> */}
          <RegionField />
          <ReferenceField
            source="address_id"
            reference="customer-address"
            sortable={false}
            link="edit"
            allowEmpty
          >
            <FunctionField
              render={(record) => (record ? printFullAddress(record) : null)}
            />
          </ReferenceField>

          <EditButton />
        </ZippityDatagrid>
      </ListWithCSVImport>
      {isUsingHelpText && (
        <div>
          The <strong>Client Locations</strong> are the different places where
          we offer services.
          <ul>
            <li>
              When you first create a location, it should have{' '}
              <strong>Is Active</strong> turned <strong>OFF</strong>. When you
              turn this on, it goes live on the website.
            </li>
            <li>
              The <strong>Name</strong> is visible to the customer.
            </li>
            <li>
              Each location has a <strong>Default {getServiceEntity()}</strong>.
              It is used to suggest a location when adding dates to the
              schedule.
            </li>
            <li>
              <strong>Address, City, State, Postal Code</strong> all tell us the
              geographical location.
            </li>
            <li>
              The <strong>Price Sheet</strong> determines what prices we show,
              for all customers who sign up at that location.
            </li>
            <li>
              The <strong>Region</strong> is displayed as a header in the{' '}
              <a href="/screenshots/location-dropdown.png" target="_blank">
                Client Location Dropdown
              </a>{' '}
              in the booking app.
            </li>
          </ul>
        </div>
      )}
    </div>
  ),
);

const editStyles = {
  service_vehicle_id: { display: 'inline-block' },
  addressSection: {
    display: 'flex',
    alignItems: 'first baseline',
  },
  addressElement: {
    margin: 10,
  },
};

const validateName = [required(), minLength(2), maxLength(255)];
const validateHours = [number(), minValue(0)];

// Custom toolbar to add address to Edit and Create payloads (used in Client Location and Service Vehicle)
export const SaveToolbarWithAddressCreation = ({
  address,
  addressIsRequired = false,
  addressHasError = false,
  pristine,
  ...rest
}) => {
  return (
    <Toolbar {...rest}>
      <SaveButton
        submitOnEnter
        disabled={addressHasError || (addressIsRequired && !address)}
        pristine={false}
        transform={(data) => {
          if (address) {
            return { ...data, address };
          }
          return data;
        }}
      />
    </Toolbar>
  );
};

export const addressValidation = (
  addressValues,
  addressErrors,
  setAddressErrors,
) => {
  const requiredFields = ['address_line_1', 'city', 'state', 'postal_code'];
  const errorFields = requiredFields.reduce((errFields, field) => {
    if (!addressValues[field]) {
      errFields.push(field);
    }
    return errFields;
  }, []);
  if (errorFields !== addressErrors) setAddressErrors(errorFields);
};

export const ClientLocationEdit = withStyles(editStyles)(
  ({ classes, ...props }) => {
    const [showNewAddress, setShowNewAddress] = useState(false);
    const [address, setAddress] = useState(null);
    const [addressErrors, setAddressErrors] = useState([]);

    const setAddressWithValidation = (addressValues) => {
      addressValidation(addressValues, addressErrors, setAddressErrors);
      setAddress(addressValues);
    };

    const handleChangeField = (event) => {
      const fieldObject = { [event.target.id]: event.target.value };
      const newAddress = {
        ...address,
        ...fieldObject,
      };
      setAddressWithValidation(newAddress);
    };

    return (
      <Edit
        undoable={false}
        title={<CustomTitle title="resources.client-location.title" />}
        {...props}
      >
        <ZippitySimpleForm
          toolbar={
            <SaveToolbarWithAddressCreation
              address={address}
              addressHasError={addressErrors.length > 0}
            />
          }
        >
          <TextInput
            validate={validateName}
            source="long_name"
            formClassName={classes.long_name}
          />
          <BooleanInput source="is_active" />
          {/* <TextInput source="logo_image_url" /> */}
          {/* <PriceSheetInput isRequired {...props} /> */}
          {hasWorkplaceServices && (
            <NumberInput validate={validateHours} source="valet_hours" />
          )}
          {hasWorkplaceServices && (
            <ReferenceInput
              validate={required()}
              source="default_mechanical_service_vehicle_id"
              reference="service-vehicle"
              sort={{ field: 'service_vehicle_id', order: 'ASC' }}
              filter={{ is_active: true }}
              perPage={perPage}
              label={`Default ${getServiceEntity()}*`}
            >
              <ReferenceChild
                {...props}
                optionText="name"
                source="default_mechanical_service_vehicle_id"
              />
            </ReferenceInput>
          )}
          <FormDataConsumer source="address_id">
            {({ formData: data, ...props }) => {
              if (!showNewAddress && !!data.address_id) {
                return (
                  <div className={classes.addressSection}>
                    <ReferenceField
                      source="address_id"
                      reference="customer-address"
                      link={false}
                      {...props}
                    >
                      <FormDataConsumer>
                        {({ record }) => {
                          if (record) {
                            return <p>Address: {printFullAddress(record)}</p>;
                          }
                        }}
                      </FormDataConsumer>
                    </ReferenceField>
                    <Link
                      onClick={() => setShowNewAddress(true)}
                      className={classes.addressElement}
                    >
                      Edit Address
                    </Link>
                  </div>
                );
              }
              return (
                <>
                  <div>Enter Address:</div>
                  <div style={{ width: '50%' }}>
                    <SearchLocationInput
                      setAddress={setAddressWithValidation}
                    />
                  </div>
                  <AddressFields
                    address={address}
                    handleChangeField={handleChangeField}
                    addressErrors={addressErrors}
                  />
                </>
              );
            }}
          </FormDataConsumer>
        </ZippitySimpleForm>
      </Edit>
    );
  },
);

export const ClientLocationCreate = withStyles(editStyles)(
  ({ classes, ...props }) => {
    const [address, setAddress] = useState(null);
    const [addressErrors, setAddressErrors] = useState([]);

    const setAddressWithValidation = (addressValues) => {
      addressValidation(addressValues, addressErrors, setAddressErrors);
      setAddress(addressValues);
    };

    const handleChangeField = (event) => {
      const fieldObject = { [event.target.id]: event.target.value };
      const newAddress = {
        ...address,
        ...fieldObject,
      };
      setAddressWithValidation(newAddress);
    };

    return (
      <Create {...props}>
        <ZippitySimpleForm
          toolbar={
            <SaveToolbarWithAddressCreation
              address={address}
              addressIsRequired
              addressHasError={addressErrors.length > 0}
            />
          }
        >
          <TextInput
            validate={validateName}
            source="long_name"
            formClassName={classes.long_name}
          />
          <BooleanInput source="is_active" />
          {/* <TextInput source="logo_image_url" /> */}
          {/* <PriceSheetInput isRequired {...props} /> */}
          {hasWorkplaceServices && (
            <NumberInput validate={validateHours} source="valet_hours" />
          )}
          {hasWorkplaceServices && (
            <ReferenceInput
              validate={required()}
              source="default_mechanical_service_vehicle_id"
              reference="service-vehicle"
              sort={{ field: 'service_vehicle_id', order: 'ASC' }}
              filter={{ is_active: true }}
              perPage={perPage}
              label={`Default ${getServiceEntity()}*`}
            >
              <ReferenceChild
                {...props}
                optionText="name"
                source="default_mechanical_service_vehicle_id"
              />
            </ReferenceInput>
          )}
          <div>Address:</div>
          <SearchLocationInput
            isRequired
            setAddress={setAddressWithValidation}
          />
          <AddressFields
            address={address}
            handleChangeField={handleChangeField}
            addressErrors={addressErrors}
          />
        </ZippitySimpleForm>
      </Create>
    );
  },
);
